<template>
  <SmDialog
    v-model:visibility="visibility"
    :title="i18n.t('export')"
    size="small">
    <template #default>
      <span> {{ i18n.t('exportSelectFormat.description') }} </span>
      <div class="format-selection">
        <div class="mb-3 flex items-center justify-between">
          <div class="flex items-center gap-2">
            <span>{{ i18n.t('exportUseCurrentFilter') }}</span>
            <SmTooltip>
              <template #content>
                {{ i18n.t('exportUseCurrentFilterHint') }}
              </template>
              <Info :size="14" class="cursor-help text-contrast-muted" />
            </SmTooltip>
          </div>
          <SmSwitch v-model="useCurrentFilter" />
        </div>

        <div class="mb-3 flex items-center justify-between">
          <span>{{ i18n.t('exportShowOneAccountPerLine') }}</span>
          <SmSwitch v-model="oneRowPerLicense" />
        </div>

        <div class="mb-6 flex items-center justify-between">
          <span>{{ i18n.t('exportIgnoreUnassignedQuota') }}</span>
          <SmSwitch v-model="ignoreUnassignedQuota" />
        </div>

        <div class="flex items-center justify-between">
          <span>{{ i18n.t('format') }}</span>
          <el-radio-group v-model="format">
            <el-radio-button :label="OutputFormat.CSV" />
            <el-radio-button :label="OutputFormat.JSON" />
            <el-radio-button :label="OutputFormat.XLSX" />
          </el-radio-group>
        </div>
      </div>
    </template>
    <template #footer>
      <div style="display: flex; gap: 1rem">
        <SmButton v-if="!loading" outline @click="visibility = false">
          {{ i18n.t('general.cancel') }}
        </SmButton>

        <SmButton :loading="loading" @click="exportData">
          {{ i18n.t('export') }}
        </SmButton>
      </div>
    </template>
  </SmDialog>
</template>

<script setup lang="ts">
  import dayjs from 'dayjs'
  import { Info } from 'lucide-vue-next'

  import { useI18n } from 'vue-i18n'

  import { ExportLicencesService, OutputFormat } from '@/client'
  import { exportResource } from '@/utils/export'
  import { getApiErrorMessage } from '@/utils/errors'
  import { sendToast } from '@/components/sm/SmNotification'
  import { usePreferenceStore } from '@/stores/preferenceStore'
  import { AccountFilters } from '@/stores/account/account.filter'

  const i18n = useI18n()
  const preferenceStore = usePreferenceStore()

  const visibility = defineModel('visibility', {
    type: Boolean,
    default: true,
  })

  const loading = ref(false)

  const format = ref<OutputFormat>(OutputFormat.CSV)

  interface Props {
    applicationId?: string
    subAccountId?: string
    accountFilter?: AccountFilters
  }

  const props = withDefaults(defineProps<Props>(), {
    applicationId: undefined,
    subAccountId: undefined,
    accountFilter: undefined,
  })

  const oneRowPerLicense = ref<boolean>(false)
  const ignoreUnassignedQuota = ref<boolean>(false)
  const useCurrentFilter = ref<boolean>(true)

  type ExportLicencesQueryParams = Parameters<
    typeof ExportLicencesService.exportLicencesApiV1ExportLicencesPost
  >[0]

  function applyAccountFilterToQueryParams(
    queryParams: ExportLicencesQueryParams,
    accountFilter?: AccountFilters
  ) {
    if (!accountFilter) return

    if (accountFilter.applications && accountFilter.applications.length > 0) {
      queryParams.softwareIdIn = accountFilter.applications
    }

    if (accountFilter.applicationId) {
      queryParams.softwareId = accountFilter.applicationId
    }

    if (accountFilter.subAccountId) {
      queryParams.subAccountId = accountFilter.subAccountId
    }

    if (accountFilter.status && accountFilter.status.length > 0) {
      if (accountFilter.status.length === 1) {
        queryParams.status = accountFilter.status[0]
      } else {
        queryParams.statusIn = accountFilter.status
      }
    }

    if (accountFilter.isGuest !== undefined) {
      queryParams.isGuest = accountFilter.isGuest
    }

    if (accountFilter.accountId) {
      queryParams.accountId = accountFilter.accountId
    }
  }

  function exportData() {
    loading.value = true
    const queryParams: ExportLicencesQueryParams = {}

    if (useCurrentFilter.value) {
      applyAccountFilterToQueryParams(queryParams, props.accountFilter)
    }

    if (props.subAccountId) {
      queryParams.subAccountId = props.subAccountId
    }
    if (props.applicationId) {
      queryParams.softwareId = props.applicationId
      delete queryParams.softwareIdIn
    }

    exportResource({
      requestBody: {
        output_format: format.value,
        one_row_per_software_license: !oneRowPerLicense.value,
        cost_config: {
          target_currency_code: preferenceStore.preferences?.currency_code,
          user_cost_ignore_unassigned_quota: ignoreUnassignedQuota.value,
        },
      },

      queryParams: queryParams,
    })
      .then(
        async (response) => {
          if (response.status !== 200) {
            loading.value = false
            return
          }
          let returnValue
          loading.value = false
          if (format.value === OutputFormat.JSON) {
            returnValue = JSON.stringify(await response.json(), null, 2)
          } else if (format.value == OutputFormat.XLSX) {
            returnValue = await response.blob()
          } else returnValue = await response.text()

          const blob = new Blob([returnValue], {
            type: 'text/plain',
          })
          const url = window.URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = url
          // Use current date as filename for the export
          // Use dayjs to format the date
          const date = dayjs()

          link.setAttribute(
            'download',
            `${date.format(
              'YYYY-MM-DD'
            )}-licenses.${format.value.toLowerCase()}`
          )
          document.body.appendChild(link)
          link.click()
          link.remove()
          visibility.value = false
          sendToast(
            i18n.t('licensesExportedSuccessfully'),
            undefined,
            'success'
          )
        },
        (error) => {
          sendToast(
            getApiErrorMessage(error, i18n as ReturnType<typeof useI18n>),
            undefined,
            'error'
          )
        }
      )
      .finally(() => {
        loading.value = false
      })
  }
</script>

<style scoped>
  .format-selection {
    margin-top: 1rem;
  }
</style>
